import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
    matchPath,
    Navigate,
    Outlet,
    Route,
    Routes,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { usePageData, PageButtonLink, PageTitle } from "src/_metronic/layout/core";
import RequiredRole from "src/app/components/auth/RequiredRole";
import {
    checkPathRequirement,
    getRequiredRoleByPath,
} from "src/app/routing/RouteRequirementHandler";
import { useAuth } from "src/app/utils/useAuth";
import SeoMetricsGrid from "./SeoMetricsGrid/SeoMetricsGrid";
import {
    useAllPrivateSitesByManagerAndUnderOptions,
    useGetAllUrlsFromMoneySite,
} from "src/app/hooks";
import { DefaultSelected } from "src/app/components";
import { find } from "lodash";
import OffSiteGrid from "./OffSiteGrid/OffSiteGrid";
import OnSiteGrid from "./OnSiteGrid/OnSiteGrid";

const SiteAnalyseManagementWrapper: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setPageToolbarLinks } = usePageData();
    const { formatMessage } = useIntl();
    const { currentUser } = useAuth();
    const { privateSitesOptions, loading } = useAllPrivateSitesByManagerAndUnderOptions();
    const {
        urlsByMoneySiteOptions,
        refresh,
        loading: loadingUrlsByMoneySite,
    } = useGetAllUrlsFromMoneySite();

    const [moneySiteId, setMoneySiteId] = useState<number>();
    const [currentUrl, setCurrentUrl] = useState<string>();

    const basePath = "/analyse/zoom/";

    const SiteSectionLinks: Array<PageButtonLink & { children: React.FC }> = [
        {
            path: "seoMetrics",
            title: formatMessage({ id: "siteAnalyse.manage.seoMetrics" }),
            isActive: false,
            isSeparator: false,
            children: () => (
                <SeoMetricsGrid
                    moneySiteId={moneySiteId}
                    siteOption={find(privateSitesOptions, (el) => el.value === moneySiteId)}
                />
            ),
        },
        {
            path: "offSite/*",
            title: formatMessage({ id: "siteAnalyse.manage.offSite" }),
            isActive: false,
            isSeparator: false,
            children: () => (
                <OffSiteGrid moneySiteId={moneySiteId} basePath={`${basePath}offSite/`} />
            ),
        },
        {
            path: "onSite/*",
            title: formatMessage({ id: "siteAnalyse.manage.onSite" }),
            isActive: false,
            hide: true,
            isSeparator: false,
            children: () => (
                <OnSiteGrid moneySiteId={moneySiteId} basePath={`${basePath}onSite/`} />
            ),
        },
    ];

    const changeMoneySite = (newMoneySiteId: number) => {
        if (moneySiteId === newMoneySiteId) return;
        setMoneySiteId(newMoneySiteId);
        setCurrentUrl(undefined);
        refresh(newMoneySiteId);
    };

    const onChangeMoneySite = (value: number) => {
        if (moneySiteId === value) return;
        window.history.replaceState(null, "", `?moneySiteId=${value}`);
        changeMoneySite(value);
    };

    const onChangeUrl = (value: string) => {
        const siteOrderType = urlsByMoneySiteOptions.find((el) => el.value === value);

        if (!siteOrderType) return;

        const urlFilters = `url[filterModels][0][filterType]=text&url[filterModels][0][type]=contains&url[filterModels][0][filter]=${value}`;

        const url = `${basePath}offSite/${siteOrderType.siteOrderType.toLowerCase()}?moneySiteId=${moneySiteId}&${urlFilters}`;
        navigate(url);
        setCurrentUrl(value);
    };

    const MemoMoneySiteSelect = useMemo(
        () => (
            <div className="toolbarDropDown">
                <DefaultSelected
                    value={moneySiteId}
                    options={privateSitesOptions}
                    isLoading={loading}
                    onChange={onChangeMoneySite}
                />
            </div>
        ),
        [privateSitesOptions, moneySiteId]
    );

    const MemoUrlSelect = useMemo(
        () => (
            <div className="toolbarDropDown urlToolBarDropDown">
                <label className="goTo">{formatMessage({ id: "common.field.goTo" })}</label>
                <div className="selectWrapper">
                    <DefaultSelected
                        value={currentUrl}
                        options={urlsByMoneySiteOptions}
                        isLoading={loadingUrlsByMoneySite}
                        onChange={onChangeUrl}
                    />
                </div>
            </div>
        ),
        [urlsByMoneySiteOptions, loadingUrlsByMoneySite, currentUrl]
    );

    useEffect(() => {
        if (moneySiteId != undefined) return;

        const moneySite = searchParams.get("moneySiteId");

        if (moneySite && privateSitesOptions.length > 1) {
            changeMoneySite(parseInt(moneySite, 10));
            return;
        }

        if (privateSitesOptions.length > 0 && typeof privateSitesOptions[0].value === "number") {
            window.history.replaceState(null, "", `?moneySiteId=${privateSitesOptions[0].value}`);
            changeMoneySite(privateSitesOptions[0].value);
        }
    }, [privateSitesOptions]);

    useEffect(() => {
        setPageToolbarLinks(
            SiteSectionLinks.map((link) => {
                let auxLink = { ...link };
                if (auxLink.hide)
                    return {
                        ...auxLink,
                        title: "",
                    };

                if (checkPathRequirement(basePath + auxLink.path, currentUser?.role)) {
                    auxLink.path = basePath + auxLink.path.replaceAll("/*", "");

                    if (link.path.includes("/*")) {
                        auxLink.isActive = location.pathname.includes(auxLink.path);
                    } else {
                        auxLink.isActive = !!matchPath(auxLink.path, location.pathname);
                    }

                    return auxLink;
                }
                auxLink = {
                    path: "",
                    title: "",
                    isActive: false,
                    isSeparator: false,
                    children: () => <></>,
                };

                return auxLink;
            })
        );

        const currentMoneySiteId = searchParams.get("moneySiteId");

        if (!moneySiteId && currentMoneySiteId) {
            changeMoneySite(parseInt(currentMoneySiteId, 10));
        }

        if (
            privateSitesOptions.length > 0 &&
            typeof privateSitesOptions[0].value === "number" &&
            !moneySiteId
        ) {
            window.history.replaceState(null, "", `?moneySiteId=${privateSitesOptions[0].value}`);
            changeMoneySite(privateSitesOptions[0].value);
        }
        if (moneySiteId && !currentMoneySiteId) {
            window.history.replaceState(null, "", `?moneySiteId=${moneySiteId}`);
        }
    }, [location]);

    return (
        <>
            {useMemo(
                () => (
                    <Routes>
                        <Route element={<Outlet />}>
                            {SiteSectionLinks.map(({ path, children }, i) => {
                                const roles = getRequiredRoleByPath(location.pathname);
                                return (
                                    <Fragment key={i}>
                                        <Route element={<RequiredRole allowedRoles={roles} />}>
                                            <Route
                                                path={path}
                                                element={
                                                    <>
                                                        <PageTitle
                                                            leftPageCustomComponent={
                                                                MemoMoneySiteSelect
                                                            }
                                                            rightPageCustomComponent={
                                                                location.pathname.includes(
                                                                    "offSite"
                                                                )
                                                                    ? MemoUrlSelect
                                                                    : undefined
                                                            }
                                                        >
                                                            {formatMessage({
                                                                id: "siteAnalyze.manage.title",
                                                            })}
                                                        </PageTitle>

                                                        <>{React.createElement(children, {})}</>
                                                    </>
                                                }
                                            />
                                        </Route>
                                    </Fragment>
                                );
                            })}
                            <Route index element={<Navigate to="offSite/lp" />} />
                        </Route>
                    </Routes>
                ),
                [
                    moneySiteId,
                    privateSitesOptions,
                    urlsByMoneySiteOptions,
                    loadingUrlsByMoneySite,
                    currentUrl,
                    location,
                ]
            )}
        </>
    );
};

export default SiteAnalyseManagementWrapper;
